import React, { useEffect, useMemo, useState } from 'react'
import { RefreshCw, CheckCircle, AlertTriangle, DollarSign } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../components/Common/Breadcrumb'
import DataTable from '../../components/DataTable/DataTable'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import subscriptionService from '../../services/subscriptionService'
import {
  defaultSubscriptionStats,
  formatCurrency,
  formatDate,
  getSubscriptionStatusClass,
  resolveMonthlyCost,
  transformSubscriptionRow,
} from './subscriptionUtils'
import '../../styles/subscriptions-tables.css'

const RenewalList = () => {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(defaultSubscriptionStats())
  const [renewItem, setRenewItem] = useState(null)
  const [showRenewModal, setShowRenewModal] = useState(false)

  useEffect(() => {
    loadAll()
  }, [])

  const loadAll = async () => {
    try {
      setLoading(true)
      const [boardRes, statsRes] = await Promise.all([
        subscriptionService.getRenewalsBoard({ days: 90 }),
        subscriptionService.getStats(),
      ])
      if (boardRes.success) {
        setRows((boardRes.data || []).map(transformSubscriptionRow))
      }
      if (statsRes.success && statsRes.data) {
        setStats({ ...defaultSubscriptionStats(), ...statsRes.data })
      }
    } catch (error) {
      if (!error._errorHandled) toast.error('Failed to load renewal data')
    } finally {
      setLoading(false)
    }
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'subscriptionNumber',
      header: 'Subscription #',
      cell: ({ getValue }) => <span className="code-text">{getValue() || '-'}</span>,
    },
    {
      accessorKey: 'subscriptionName',
      header: 'Subscription',
      cell: ({ getValue, row }) => (
        <div>
          <div className="description-text">{getValue() || '-'}</div>
          <div style={{ fontSize: 12, color: '#5B6478' }}>{row.original.vendorName || row.original.provider || '—'}</div>
        </div>
      ),
    },
    {
      accessorKey: 'planName',
      header: 'Current plan',
      cell: ({ getValue }) => getValue() || '—',
    },
    {
      accessorKey: 'expiryDate',
      header: 'Expiry',
      meta: { align: 'center' },
      cell: ({ row }) => formatDate(row.original.expiryDate || row.original.endDate),
    },
    {
      accessorKey: 'renewalDate',
      header: 'Renewal date',
      meta: { align: 'center' },
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      accessorKey: 'currentCost',
      header: 'Current cost',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const amount = Number(row.original.currentCost) || resolveMonthlyCost(row.original)
        return (
          <div className={`sub-cost${amount ? '' : ' sub-cost--zero'}`}>
            <span className="sub-cost__main">{formatCurrency(amount, row.original.currency)}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'proposedRenewalCost',
      header: 'Proposed',
      meta: { align: 'right' },
      cell: ({ row }) => formatCurrency(row.original.proposedRenewalCost || 0, row.original.currency),
    },
    {
      accessorKey: 'costDelta',
      header: 'Change',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const d = Number(row.original.costDelta) || 0
        const color = d > 0 ? '#B42318' : d < 0 ? '#027A48' : '#5B6478'
        return <span style={{ color, fontWeight: 650 }}>{formatCurrency(d, row.original.currency)}</span>
      },
    },
    {
      accessorKey: 'autoRenewal',
      header: 'Auto-renew',
      meta: { align: 'center' },
      cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
    },
    {
      accessorKey: 'renewal',
      header: 'Decision',
      cell: ({ row }) => row.original.renewal?.decision || 'Pending decision',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: { align: 'center' },
      cell: ({ getValue }) => (
        <span className={`sub-status ${getSubscriptionStatusClass(getValue())}`}>{getValue() || '-'}</span>
      ),
    },
  ], [])

  const handleRenew = async () => {
    if (!renewItem) return
    try {
      const response = await subscriptionService.renew(renewItem.id || renewItem._id, {
        decision: renewItem.renewal?.decision || 'Renew',
        proposedRenewalCost: renewItem.renewal?.proposedRenewalCost,
      })
      if (response.success) {
        toast.success('Subscription renewed')
        setShowRenewModal(false)
        setRenewItem(null)
        loadAll()
      } else toast.error(response.message || 'Renewal failed')
    } catch (error) {
      if (!error._errorHandled) toast.error('Renewal failed')
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={[
        { label: 'Subscriptions', path: '/subscriptions', onClick: () => navigate('/subscriptions') },
        { label: 'Renewals', isCurrent: true },
      ]} />
      <div className="page-header">
        <div className="page-title">
          <h1>Renewals</h1>
          <p>Review upcoming renewals, cost changes and approval decisions. Reminders do not mark renewals complete.</p>
        </div>
      </div>

      <div className="dashboard-metrics-grid" style={{ marginBottom: 16 }}>
        <div className="metric-card"><div className="metric-icon"><RefreshCw size={16} /></div><div className="metric-value">{stats.renewed}</div><div className="metric-title">Previously renewed</div></div>
        <div className="metric-card"><div className="metric-icon"><AlertTriangle size={16} /></div><div className="metric-value">{stats.expiring}</div><div className="metric-title">Due in 30 days</div></div>
        <div className="metric-card"><div className="metric-icon"><DollarSign size={16} /></div><div className="metric-value">{formatCurrency(stats.monthlyCost)}</div><div className="metric-title">Monthly committed</div></div>
        <div className="metric-card"><div className="metric-icon"><CheckCircle size={16} /></div><div className="metric-value">{rows.filter((r) => r.renewal?.decision === 'Pending decision').length}</div><div className="metric-title">Pending decision</div></div>
      </div>

      <DataTable
        data={rows}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search renewals…"
        onView={(row) => navigate(`/subscriptions/view/${row.id || row._id}`)}
        onEdit={(row) => navigate(`/subscriptions/edit/${row.id || row._id}`)}
        customActions={[
          {
            label: 'Complete renewal',
            onClick: (row) => { setRenewItem(row); setShowRenewModal(true) },
          },
        ]}
      />

      <ConfirmationModal
        isOpen={showRenewModal}
        onClose={() => { setShowRenewModal(false); setRenewItem(null) }}
        onConfirm={handleRenew}
        title="Complete renewal"
        message={`Mark renewal complete for ${renewItem?.subscriptionName || 'this subscription'}? This extends validity and does not create a payment by itself.`}
        confirmText="Complete renewal"
      />
    </div>
  )
}

export default RenewalList
